import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { planningApi } from '@/apis/planning.api'
import { KPI_METRIC_REGISTRY, SIGNAL_METRIC_META } from '@/composables/dashboard-kpi'
import { useWebSiteStore } from '@/stores/web-site.store'

export const initDashboardStoreAsync = async() => {}

// Mirrors backend planning.DashboardTileKind.
export const dashboardTileKindEnum = Object.freeze({
  KPI_METRIC: 0,
  KPI_CHART: 1,
  TEMPLATE_CHART: 2,
})

export const useDashboardStore = defineStore('dashboard', () => {
  const tiles = ref([])
  const kpiMetrics = ref([])
  const templateCharts = ref([])
  const defaultLayouts = ref([])
  const loaded = ref(false)

  const fetchDashboardCatalogAsync = async(force = false) => {
    if (loaded.value && !force) return
    const data = (await planningApi.getDashboardCatalogAsync()).data
    tiles.value = data.tiles ?? []
    kpiMetrics.value = data.kpi_metrics ?? []
    templateCharts.value = data.template_charts ?? []
    defaultLayouts.value = data.default_layouts ?? []
    loaded.value = true
  }

  const orderedTiles = computed(() => [...tiles.value].sort((a, b) => a.order - b.order))

  const resolvePageTiles = (page) => {
    const toTile = (t) => ({ id: t.tile_key, title: t.title_key, x: t.default_x, y: t.default_y, w: t.default_w, h: t.default_h })
    const layout = defaultLayouts.value.find(l => l.page === page)
    if (!layout || !layout.items?.length) return orderedTiles.value.map(toTile)
    const metaByKey = Object.fromEntries(tiles.value.map(t => [t.tile_key, t]))
    return layout.items.map(it => ({
      id: it.tile_key,
      title: metaByKey[it.tile_key]?.title_key ?? it.tile_key,
      x: it.x, y: it.y, w: it.w, h: it.h,
    }))
  }

  // Merge backend catalog metadata with the frontend-only { fetch, format } registry.
  const isMetricAllowed = (metricKey) => {
    const permission = SIGNAL_METRIC_META[metricKey]?.permission
    return permission === undefined || useWebSiteStore().can(permission)
  }

  const kpiMetricsMap = computed(() =>
    Object.fromEntries(kpiMetrics.value.filter(m => isMetricAllowed(m.metric_key)).map(m => [m.metric_key, {
      titleKey: m.title_key,
      labelKey: m.label_key,
      additionalLabelKey: m.additional_label_key,
      supportsWarehouseFilter: m.supports_warehouse_filter,
      supportsPeriodFilter: m.supports_period_filter,
      link: m.link_route ? { name: m.link_route } : null,
      linkLabelKey: m.link_label_key,
      ...(SIGNAL_METRIC_META[m.metric_key] ?? {}),
      ...(KPI_METRIC_REGISTRY[m.metric_key] ?? {}),
    }]))
  )

  const kpiMetric = (key) => kpiMetricsMap.value[key]

  const kpiTileIds = computed(() =>
    new Set(tiles.value.filter(t => t.tile_kind === dashboardTileKindEnum.KPI_METRIC).map(t => t.tile_key))
  )
  const kpiChartTileIds = computed(() =>
    new Set(tiles.value.filter(t => t.tile_kind === dashboardTileKindEnum.KPI_CHART).map(t => t.tile_key))
  )
  const kpiDefaultByTile = computed(() =>
    Object.fromEntries(tiles.value
      .filter(t => t.tile_kind === dashboardTileKindEnum.KPI_METRIC)
      .map(t => [t.tile_key, t.default_metric_key]))
  )
  const kpiChartDefaultByTile = computed(() =>
    Object.fromEntries(tiles.value
      .filter(t => t.tile_kind === dashboardTileKindEnum.KPI_CHART)
      .map(t => [t.tile_key, t.default_metric_key]))
  )

  const complexCharts = computed(() =>
    [...templateCharts.value]
      .sort((a, b) => a.order - b.order)
      .map(c => ({
        id: c.chart_key,
        titleKey: c.title_key,
        icon: c.icon,
        supportsWarehouseFilter: c.supports_warehouse_filter,
        supportsPeriodFilter: c.supports_period_filter,
      }))
  )

  return {
    tiles,
    kpiMetrics,
    templateCharts,
    defaultLayouts,
    dashboardTileKindEnum,
    fetchDashboardCatalogAsync,
    resolvePageTiles,
    kpiMetricsMap,
    kpiMetric,
    kpiTileIds,
    kpiChartTileIds,
    kpiDefaultByTile,
    kpiChartDefaultByTile,
    complexCharts,
  }
})
